/**
 * EventPage Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive page for displaying individual event information with:
 * - Dynamic event fetching by ID from URL parameters
 * - Event navigation with next/previous functionality
 * - EventsModal integration in page mode
 * - Date processing and validation
 * - Event topics array conversion and management
 * - Navigation controls with event previews
 * - Loading states and error handling
 * - Responsive design for various screen sizes
 * - Back to events functionality
 * 
 * Features:
 * - URL parameter-based event loading
 * - Event navigation with preview titles
 * - Error handling for invalid events
 * - Date normalization and processing
 * - Topics array conversion from comma-separated strings
 * - Loading spinners for better UX
 * 
 * @returns {JSX.Element} EventPage component with event details
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../css/Colors.css";
import apiHelper from "../utils/apiHelper";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import EventsModal from "../modals/EventsModal";
import "./EventPage.css";

const { FILE_NAMES } = require('../utils/constants');

/**
 * EventPage component for displaying detailed event information
 * with navigation and modal integration
 */
function EventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [navigationLoading, setNavigationLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextEventId, setNextEventId] = useState(null);
  const [previousEventId, setPreviousEventId] = useState(null);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all events data
        const eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
        const events = eventsResult.fileContent;
        
        // Process events (same logic as Events.js)
        events.forEach((event) => {
          let date;
          try {
            const raw_date = new Date(event.EventDate);
            
            if (isNaN(raw_date.getTime())) {
              date = raw_date;
            } else {
              const hour = raw_date.getUTCHours();
              const shiftDay = hour >= 12 ? 1 : 0;

              date = new Date(
                Date.UTC(
                  raw_date.getUTCFullYear(),
                  raw_date.getUTCMonth(),
                  raw_date.getUTCDate() + shiftDay,
                  3
                )
              );
            }
          } catch (error) {
            date = new Date(event.EventDate);
          }

          event.EventDate = date;
          
          // Convert Topics from comma-separated string to array
          if (event.Topics) {
            if (typeof event.Topics === 'string') {
              event.Topics = event.Topics.split(',').map(topic => topic.trim()).filter(topic => topic);
            }
          }
        });

        setAllEvents(events);
        
        // Find the specific event by ID
        const event = events.find(e => e.ID && e.ID.toString() === id);
        
        if (!event) {
          setError(`Event with ID ${id} not found`);
        } else {
          setEventData(event);
          setupNavigation(events, event);
        }
        
      } catch (err) {
        setError("Failed to load event data");
        console.error("Error fetching event data:", err);
      } finally {
        setIsLoading(false);
        setNavigationLoading(false);
      }
    };

    if (id) {
      fetchEventData();
    } else {
      setError("No event ID provided");
      setIsLoading(false);
    }
  }, [id]);

  const setupNavigation = (allEvents, currentEvent) => {
    const currentIndex = allEvents.findIndex(event => event.ID === currentEvent.ID);
    
    if (currentIndex > 0) {
      setPreviousEventId(allEvents[currentIndex - 1].ID);
    } else {
      setPreviousEventId(null);
    }
    
    if (currentIndex < allEvents.length - 1) {
      setNextEventId(allEvents[currentIndex + 1].ID);
    } else {
      setNextEventId(null);
    }
  };

  const handleBackToEvents = () => {
    navigate('/Events');
  };

  const handleNavigation = (targetEventId) => {
    if (targetEventId) {
      navigate(`/events/${targetEventId}`);
    }
  };

  const getNextEvent = () => {
    if (!allEvents || !nextEventId) return null;
    return allEvents.find(event => event.ID === nextEventId);
  };

  const getPreviousEvent = () => {
    if (!allEvents || !previousEventId) return null;
    return allEvents.find(event => event.ID === previousEventId);
  };

  const handleNext = () => {
    const nextEvent = getNextEvent();
    if (nextEvent) {
      navigate(`/events/${nextEvent.ID}`);
    }
  };

  const handlePrevious = () => {
    const previousEvent = getPreviousEvent();
    if (previousEvent) {
      navigate(`/events/${previousEvent.ID}`);
    }
  };

  if (isLoading) {
    return (
      <div className="event-page-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-page-error">
        <div className="error-container">
          <h2>Event Not Found</h2>
          <p>{error}</p>
          <button onClick={handleBackToEvents} className="btn btn-outline-primary">
            <i className="fas fa-arrow-left"></i> Events
          </button>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="event-page-error">
        <div className="error-container">
          <h2>Event Not Found</h2>
          <p>The requested event could not be found.</p>
          <button onClick={handleBackToEvents} className="btn btn-outline-primary">
            <i className="fas fa-arrow-left"></i> Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-page">
      {/* Page Header */}
      <div className="event-page-header">
        <div className="container">
          <div className="event-page-nav">
            <button className="btn btn-outline-primary" onClick={handleBackToEvents}>
              <i className="fas fa-arrow-left"></i> Events
            </button>
            
            <div className="event-page-nav-controls">
              {navigationLoading && (
                <div className="navigation-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  Loading navigation...
                </div>
              )}
              
              {!navigationLoading && previousEventId && (
                <button 
                  className="btn btn-outline-secondary nav-preview-btn"
                  onClick={() => handleNavigation(previousEventId)}
                >
                  <div className="nav-preview-content">
                    <div className="nav-direction">
                      <i className="fas fa-chevron-left"></i> Previous
                    </div>
                    <div className="nav-preview-title">
                      {getPreviousEvent()?.Title?.length > 40 
                        ? getPreviousEvent()?.Title.substring(0, 40) + '...' 
                        : getPreviousEvent()?.Title}
                    </div>
                  </div>
                </button>
              )}
              
              {!navigationLoading && nextEventId && (
                <button 
                  className="btn btn-outline-secondary nav-preview-btn"
                  onClick={() => handleNavigation(nextEventId)}
                >
                  <div className="nav-preview-content">
                    <div className="nav-direction">
                      Next <i className="fas fa-chevron-right"></i>
                    </div>
                    <div className="nav-preview-title">
                      {getNextEvent()?.Title?.length > 40 
                        ? getNextEvent()?.Title.substring(0, 40) + '...' 
                        : getNextEvent()?.Title}
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mt-4">
        {/* Event Title */}
        {eventData && (
          <div className="event-page-title-section">
            <h1 className="event-page-main-title">{eventData.Title}</h1>
          </div>
        )}
        
        <div className="event-page-content">
          <EventsModal
            data={eventData}
            show={false}
            onClose={handleBackToEvents}
            allowFullscreen={true}
            small={false}
            onNext={handleNext}
            onPrevious={handlePrevious}
            hasNext={!!getNextEvent()}
            hasPrevious={!!getPreviousEvent()}
            nextEvent={getNextEvent()}
            previousEvent={getPreviousEvent()}
            pageMode={true}
          />
        </div>
      </div>
    </div>
  );
}

export default EventPage; 